// One-phone pass-and-play: the regular game engine with every seat on a single device, plus the bits
// that decide whose turn it is to hold the phone. The server runs the game so the phone never holds
// the deck; createLocalGame runs the same setup in memory for tests.
import { Game, GameError, createRoomState, sanitizeSettings, validateName, type PlayerState, type RoomState } from './engine';
import { DEFAULT_SETTINGS, MIN_PLAYERS } from './rules';
import type { CardData, RoomSettings } from './types';

export const ONE_PHONE_MIN = MIN_PLAYERS;
export const ONE_PHONE_MAX = 10;
export const LOCAL_ROOM_CODE = 'SOLO';

export interface LocalSetup {
  names: string[];
  packIds: string[];
  pointsToWin: number;
  handSize?: number;
  writeIns?: number;
  redraws?: number;
}

export interface LocalDeps {
  random?: () => number;
  now?: () => number;
  newId?: () => string;
}

/** Card lookup for the engine from the cards of the chosen packs. */
export function lookupFrom(cards: CardData[]): (id: string) => CardData | undefined {
  const byId = new Map(cards.map((c) => [c.id, c]));
  return (id) => byId.get(id);
}

/** Clean up and check the names typed on the setup screen. Returns an error message or the names. */
export function checkNames(raw: string[]): { names: string[] } | { error: string } {
  const names: string[] = [];
  for (const r of raw) {
    if (!String(r ?? '').trim()) continue;
    try {
      names.push(validateName(r));
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Check the names.' };
    }
  }
  if (names.length < ONE_PHONE_MIN) return { error: `Add at least ${ONE_PHONE_MIN} players.` };
  if (names.length > ONE_PHONE_MAX) return { error: `One phone fits up to ${ONE_PHONE_MAX} players. Start a room for bigger groups.` };
  const seen = new Set<string>();
  for (const n of names) {
    if (seen.has(n.toLowerCase())) return { error: `Two players are called ${n}. Add an initial or a nickname.` };
    seen.add(n.toLowerCase());
  }
  return { names };
}

/** Room settings for a one-phone game. */
export function onePhoneSettings(setup: Omit<LocalSetup, 'names'>): RoomSettings {
  return sanitizeSettings({
    ...DEFAULT_SETTINGS,
    handSize: setup.handSize ?? DEFAULT_SETTINGS.handSize,
    writeIns: setup.writeIns ?? 1,
    redraws: setup.redraws ?? DEFAULT_SETTINGS.redraws,
    pointsToWin: setup.pointsToWin,
    // One phone means one judge flipping cards; a round timer would only rush the handovers.
    judgeMode: 'judge',
    roundTimer: 0,
    packIds: setup.packIds,
  });
}

/** Seat everyone, deal, and start round one. Throws GameError when the packs can't cover the table. */
export function createLocalGame(setup: LocalSetup, cards: CardData[], deps: LocalDeps = {}): Game {
  const checked = checkNames(setup.names);
  if ('error' in checked) throw new GameError(checked.error);
  const now = deps.now ?? Date.now;
  const settings = onePhoneSettings(setup);
  const game = new Game(createRoomState(LOCAL_ROOM_CODE, settings, now()), { lookup: lookupFrom(cards), ...deps });
  for (const name of checked.names) game.addPlayer({ name, token: `local-${name}` });
  game.start(game.state.hostId!, cards);
  game.drainEvents();
  return game;
}

/** Who still has to play a card this round, going round the table from the judge's left. */
export function nextToPlay(state: RoomState): PlayerState | null {
  const round = state.round;
  if (state.phase !== 'submitting' || !round) return null;
  const players = state.players;
  const judgeAt = players.findIndex((p) => p.id === round.judgeId);
  for (let i = 1; i <= players.length; i++) {
    const p = players[(judgeAt + i + players.length) % players.length];
    if (p.id !== round.judgeId && !round.submissions.some((s) => s.playerId === p.id)) return p;
  }
  return null;
}

/** Screen-level state that isn't part of the game rules. */
export interface LocalUi {
  /** The round whose prompt has been shown to the whole table. */
  introRound: number;
  /** Player whose private screen is open; null means the phone is covered. */
  unlocked: string | null;
}

export type LocalScreen =
  | { kind: 'intro'; judge: PlayerState | null }
  | { kind: 'handoff'; player: PlayerState; role: 'play' | 'judge' }
  | { kind: 'play'; player: PlayerState }
  | { kind: 'judge'; player: PlayerState }
  | { kind: 'round_over' }
  | { kind: 'game_over' }
  | { kind: 'stuck' };

/** What the phone should show right now. Private screens only open for the player they belong to. */
export function screenFor(state: RoomState, ui: LocalUi): LocalScreen {
  const round = state.round;
  const judge = round ? (state.players.find((p) => p.id === round.judgeId) ?? null) : null;
  switch (state.phase) {
    case 'submitting': {
      if (!round) return { kind: 'stuck' };
      if (ui.introRound !== round.number) return { kind: 'intro', judge };
      const next = nextToPlay(state);
      if (!next) return { kind: 'stuck' };
      return ui.unlocked === next.id ? { kind: 'play', player: next } : { kind: 'handoff', player: next, role: 'play' };
    }
    case 'judging':
      if (!judge) return { kind: 'stuck' };
      return ui.unlocked === judge.id ? { kind: 'judge', player: judge } : { kind: 'handoff', player: judge, role: 'judge' };
    case 'round_over':
      return { kind: 'round_over' };
    case 'game_over':
      return { kind: 'game_over' };
    default:
      return { kind: 'stuck' };
  }
}
